import React, { useState } from 'react';
import styled from 'styled-components';
import {
  PageLayout,
  MainContent,
  ContentContainer,
  SectionContainer,
  PageTitle,
  SectionTitle,
  FormGroup,
  FormActions,
  FlexContainer,
} from '../../styles/SharedStyles';
import ExpertSidebar from '../../components/expert/ExpertSidebar';
import {
  Switch,
  FormControlLabel,
  TextField,
  Button,
  FormGroup as MuiFormGroup,
  Box,
  Typography,
  Grid,
  IconButton,
  Alert,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Dialog,
  DialogContent,
  DialogTitle,
} from '@mui/material';
import { DatePicker, TimePicker } from '@mui/x-date-pickers';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import GoogleIcon from '@mui/icons-material/Google';
import MicrosoftIcon from '@mui/icons-material/Microsoft';
import {
  Close as CloseIcon,
  HelpOutline as HelpOutlineIcon,
} from '@mui/icons-material';

interface DateRange {
  startDate: Date | null;
  endDate: Date | null;
  reason: string;
}

interface DailyBlock {
  startTime: Date | null;
  endTime: Date | null;
  days: string[];
}

interface MeetSettings {
  enableMeetings: boolean;
  videoLink: string;
  countryCode: string;
  phoneNumber: number;
  notifications: {
    email: boolean;
    sendgrid: boolean;
  };
  calendarSync: {
    google: boolean;
    exchange: boolean;
  };
  selectedDate: Date | null;
  dateRanges: DateRange[];
  dailyBlocks: DailyBlock[];
}

const MeetSettingsPage: React.FC = () => {
  const [settings, setSettings] = useState<MeetSettings>({
    enableMeetings: false,
    videoLink: '',
    countryCode: "+61",
    phoneNumber: 412345678,
    notifications: {
      email: false,
      sendgrid: false,
    },
    calendarSync: {
      google: false,
      exchange: false,
    },
    selectedDate: null,
    dateRanges: [],
    dailyBlocks: []
  });

  const [showMeetingHelp, setShowMeetingHelp] = useState(false);

  const handleSettingChange = (setting: string, value: any) => {
    setSettings(prev => ({
      ...prev,
      [setting]: value
    }));
  };

  const handleAddDateRange = () => {
    setSettings(prev => ({
      ...prev,
      dateRanges: [...prev.dateRanges, { startDate: null, endDate: null, reason: '' }]
    }));
  };

  const handleRemoveDateRange = (index: number) => {
    setSettings(prev => ({
      ...prev,
      dateRanges: prev.dateRanges.filter((_, i) => i !== index)
    }));
  };

  const handleAddDailyBlock = () => {
    setSettings(prev => ({
      ...prev,
      dailyBlocks: [...prev.dailyBlocks, { startTime: null, endTime: null, days: [] }]
    }));
  };

  const handleRemoveDailyBlock = (index: number) => {
    setSettings(prev => ({
      ...prev,
      dailyBlocks: prev.dailyBlocks.filter((_, i) => i !== index)
    }));
  };

  const handleDayToggle = (blockIndex: number, day: string) => {
    const updatedBlocks = [...settings.dailyBlocks];
    const currentDays = updatedBlocks[blockIndex].days;
    if (currentDays.includes(day)) {
      updatedBlocks[blockIndex].days = currentDays.filter(d => d !== day);
    } else {
      updatedBlocks[blockIndex].days = [...currentDays, day];
    }
    setSettings(prev => ({
      ...prev,
      dailyBlocks: updatedBlocks
    }));
  };

  const handleOpenMeetingHelp = () => {
    setShowMeetingHelp(true);
  }

  const handleCloseMeetingHelp = () => {
    setShowMeetingHelp(false);
  }

  return (
    <PageLayout>
      <ExpertSidebar />
      <MainContent>
        <ContentContainer>
          <PageTitle variant="h4" fontWeight={600} gutterBottom>Meeting Settings</PageTitle>

          <SectionContainer>
            <Grid alignItems="flex-end" container>
              <Grid item xs={12} md={6}>
                <FormGroup>
                  <Box display="flex" justifyContent="space-between">
                    <FormControlLabel
                      control={
                        <Switch
                          checked={settings.enableMeetings}
                          onChange={(e) => handleSettingChange('enableMeetings', e.target.checked)}
                        />
                      }
                      label="Enable Meeting Bookings"
                    />
                    <IconButton onClick={handleOpenMeetingHelp}>
                      <HelpOutlineIcon color="primary" />
                    </IconButton>
                  </Box>
                  <TextField
                    fullWidth
                    label="Video Meeting Link"
                    value={settings.videoLink}
                    onChange={(e) => handleSettingChange('videoLink', e.target.value)}
                    sx={{ mt: 2 }}
                    placeholder="Enter your default video meeting link"
                  />
                </FormGroup>
              </Grid>
            </Grid>
            <Grid alignItems="flex-end" container spacing={1}>
              <Grid item xs={4} md={2}>
                <FormGroup>
                  <FormControl fullWidth>
                    <InputLabel>Country Code</InputLabel>
                    <Select
                      labelId="country-code"
                      value={settings.countryCode}
                      label="Country Code"
                      onChange={(e) => setSettings({ ...settings, countryCode: e.target.value })}
                      fullWidth
                    >
                      <MenuItem value="+61">+61</MenuItem>
                    </Select>
                  </FormControl>
                </FormGroup>
              </Grid>
              <Grid item xs={8} md={4}>
                <FormGroup>
                  <TextField
                    fullWidth
                    label="Phone Meeting Number"
                    value={settings.phoneNumber}
                    onChange={(e) => handleSettingChange('phoneNumber', e.target.value)}
                    sx={{ mt: 2 }}
                    placeholder="Phone Meeting Number"
                  />
                </FormGroup>
              </Grid>
            </Grid>
          </SectionContainer>

          <SectionContainer>
            <SectionTitle>Recurring work week</SectionTitle>
            <FlexContainer>
              <Button startIcon={<AddIcon />} variant="contained" onClick={handleAddDailyBlock}>
                Add Time Block
              </Button>
            </FlexContainer>

            {settings.dailyBlocks.map((block, index) => (
              <Box key={index} sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={4}>
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                      <TimePicker
                        label="Start Time"
                        value={block.startTime}
                        onChange={(time) => {
                          const updated = [...settings.dailyBlocks];
                          updated[index].startTime = time;
                          setSettings(prev => ({
                            ...prev,
                            dailyBlocks: updated
                          }));
                        }}
                        sx={{ width: '100%' }}
                      />
                    </LocalizationProvider>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                      <TimePicker
                        label="End Time"
                        value={block.endTime}
                        onChange={(time) => {
                          const updated = [...settings.dailyBlocks];
                          updated[index].endTime = time;
                          setSettings(prev => ({
                            ...prev,
                            dailyBlocks: updated
                          }));
                        }}
                        sx={{ width: '100%' }}
                      />
                    </LocalizationProvider>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Box>
                      {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day) => (
                        <Button
                          key={day}
                          variant={block.days.includes(day) ? 'contained' : 'outlined'}
                          size="small"
                          onClick={() => handleDayToggle(index, day)}
                          sx={{ mr: 0.5, mb: 0.5 }}
                        >
                          {day}
                        </Button>
                      ))}
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={1}>
                    <IconButton onClick={() => handleRemoveDailyBlock(index)} color="error">
                      <DeleteIcon />
                    </IconButton>
                  </Grid>
                </Grid>
              </Box>
            ))}
          </SectionContainer>

          <SectionContainer>
            <SectionTitle>Leave / Blocked out times</SectionTitle>
            <FlexContainer>
              <Button startIcon={<AddIcon />} variant="contained" onClick={handleAddDateRange}>
                Add Date Range
              </Button>
            </FlexContainer>

            {settings.dateRanges.map((range, index) => (
              <Box key={index} sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Grid container spacing={3} alignItems="center">
                  <Grid item xs={12} md={4}>
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                      <DatePicker
                        label="Start Date"
                        value={range.startDate}
                        onChange={(date) => {
                          const updated = [...settings.dateRanges];
                          updated[index].startDate = date;
                          setSettings(prev => ({
                            ...prev,
                            dateRanges: updated
                          }));
                        }}
                        sx={{ width: '100%' }}
                      />
                    </LocalizationProvider>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                      <DatePicker
                        label="End Date"
                        value={range.endDate}
                        onChange={(date) => {
                          const updated = [...settings.dateRanges];
                          updated[index].endDate = date;
                          setSettings(prev => ({
                            ...prev,
                            dateRanges: updated
                          }));
                        }}
                        sx={{ width: '100%' }}
                      />
                    </LocalizationProvider>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      label="Reason"
                      value={range.reason}
                      onChange={(e) => {
                        const updated = [...settings.dateRanges];
                        updated[index].reason = e.target.value;
                        setSettings(prev => ({
                          ...prev,
                          dateRanges: updated
                        }));
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={1}>
                    <IconButton onClick={() => handleRemoveDateRange(index)} color="error">
                      <DeleteIcon />
                    </IconButton>
                  </Grid>
                </Grid>
              </Box>
            ))}
          </SectionContainer>

          {/* <SectionContainer>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Box>
                  <Typography variant="subtitle1" gutterBottom>SendGrid Integration</Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Connect SendGrid to send branded emails to your customers
                  </Typography>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={settings.notifications.sendgrid}
                        onChange={(e) => {
                          setSettings(prev => ({
                            ...prev,
                            notifications: {
                              ...prev.notifications,
                              sendgrid: e.target.checked
                            }
                          }));
                        }}
                      />
                    }
                    label="Enable SendGrid Integration"
                  />
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                {settings.notifications.sendgrid && (
                  <Box sx={{ mt: 2 }}>
                    <TextField
                      fullWidth
                      label="SendGrid API Key"
                      type="password"
                      placeholder="Enter your SendGrid API key"
                      sx={{ mb: 2 }}
                    />
                    <TextField
                      fullWidth
                      label="Sender Email"
                      placeholder="Enter the email address you want to send from"
                      sx={{ mb: 2 }}
                    />
                    <Button variant="contained" color="primary">
                      Verify SendGrid Connection
                    </Button>
                  </Box>
                )}
              </Grid>
            </Grid>
          </SectionContainer> */}

          <SectionContainer>
            <SectionTitle>Calendar Synchronization</SectionTitle>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <GoogleIcon sx={{ mr: 2 }} />
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle1">Google Calendar</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Sync your availability with Google Calendar
                    </Typography>
                  </Box>
                  <Switch
                    checked={settings.calendarSync.google}
                    onChange={(e) => handleSettingChange('calendarSync', {
                      ...settings.calendarSync,
                      google: e.target.checked
                    })}
                  />
                </Box>
                {settings.calendarSync.google && (
                  <Alert severity="info" sx={{ mt: 1 }}>
                    Your Google Calendar is synced. Events will be automatically blocked.
                  </Alert>
                )}
              </Grid>
              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <MicrosoftIcon sx={{ mr: 2 }} />
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle1">Microsoft Exchange Calendar</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Sync your availability with Microsoft Exchange
                    </Typography>
                  </Box>
                  <Switch
                    checked={settings.calendarSync.exchange}
                    onChange={(e) => handleSettingChange('calendarSync', {
                      ...settings.calendarSync,
                      exchange: e.target.checked
                    })}
                  />
                </Box>
                {settings.calendarSync.exchange && (
                  <Alert severity="info" sx={{ mt: 1 }}>
                    Your Microsoft Calendar is synced. Events will be automatically blocked.
                  </Alert>
                )}
              </Grid>
            </Grid>
          </SectionContainer>

          <FormActions>
            <Button variant="outlined">Cancel</Button>
            <Button variant="contained" color="primary">
              Save Settings
            </Button>
          </FormActions>
        </ContentContainer>
      </MainContent>
      <Dialog onClose={handleCloseMeetingHelp} open={showMeetingHelp}>
        <DialogTitle sx={{ m: 0, p: 2 }} id="help-dialog-title">
          Meeting Bookings
        </DialogTitle>
        <IconButton
          aria-label='close'
          onClick={handleCloseMeetingHelp}
          sx={{
            position: "absolute",
            right: 8,
            top: 8
          }}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent>
          <Typography mb={3}><b>"Video Meeting Link"</b> is the online location sent to customers to join scheduled meetings.</Typography>
          <Typography>The link you provide here can be copied from any online video call supplier you currently use (BYO Link)</Typography>
          <Typography mb={3}>Eg.Google Meet, MS Teams, Zoom, WebEx, FaceTime, Skype etc. (Triplink.vip do not take responsibility for video call service quality associated with the the provider you choose.)</Typography>
          <Typography mb={3}>The <b>"Phone Meeting Number"</b> will also be provided incase there are connectivity problems with the video call link you provide.</Typography>
          <Typography><b>NOTE:</b> If a Video Link is not provided here the Phone Number will be included as the primary contact method sent to the customer and meetings will be voice all only.</Typography>
        </DialogContent>
      </Dialog>
    </PageLayout>
  );
};

export default MeetSettingsPage;
